import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import InfoIcon from "@mui/icons-material/Info";

import HimsSystemHistoryDialog from "./hmsSystemHistoryDialog";
import HimsMapPreview from "./hmsMapPreview";
import ResponsiveGrid from "../grid/responsiveGrid";
import HimsFaultDialog from "./hmsFaultDialog";
import HimsLatencyDialog from "./hmsLatencyDialog";

import { useUserAuth } from "../contexts/authContext";

import { GridActionsCellItem } from "@mui/x-data-grid";

import { CustomPagination } from "../customPagination";
import { ConvertISOToDate } from "../util";
import {
    getSystemColorFromType,
    getNameFromHimsSystem,
    ONLINE_COLOR,
    DEGRADED_COLOR,
    DEGRADED_OUTAGE_COLOR,
    FAILURE_COLOR,
    getSensorStateColor
} from "./hmsUtil";

const HimsSystemCard = ({ system }) => {
    const [expanded, setExpanded] = useState(false);
    const [components, setComponents] = useState([]);

    const [mapPreviewSystem, setMapPreviewSystem] = useState(null);
    const [systemHistoryDialogOpen, setSystemHistoryDialogOpen] = useState(false);
    const [selectedFaultComponent, setSelectedFaultComponent] = useState(null);
    const [selectedLatencyComponent, setSelectedLatencyComponent] = useState(null);

    const { socket } = useUserAuth();

    const columns = [
        {
            field: "name",
            headerName: "Name",
            flex: 1
        },
        {
            field: "sensor_state",
            headerName: "Status",
            flex: 1,
            editable: false,
            renderCell: ({ row }) => {
                const color = getSensorStateColor(row.sensor_state);
                return (
                    <Box
                        sx={{
                            fontSize: 14,
                            fontWeight: "bold",
                            color: color === "#2f2f2f" ? "#FFFFFF" : color
                        }}
                    >
                        {row.sensor_state}
                    </Box>
                );
            }
        },
        {
            field: "severity",
            headerName: "Fault State",
            flex: 1
        },
        {
            type: "actions",
            field: "actions",
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => {
                const actions = [];
                if (Array.isArray(row.faults) && row.faults.length > 0) {
                    const viewFaultsButton = (
                        <Tooltip key={1} title="View Faults">
                            <GridActionsCellItem icon={<InfoIcon />} label="View Faults" onClick={() => setSelectedFaultComponent(row)} />
                        </Tooltip>
                    );
                    actions.push(viewFaultsButton);
                }
                const oneHourAgo = dayjs().subtract(1, "hours");
                if (dayjs(row.latency_date).isAfter(oneHourAgo)) {
                    const viewLatencyButton = (
                        <Tooltip key={2} title="View Latency">
                            <GridActionsCellItem icon={<ShowChartIcon />} label="View Latency" onClick={() => setSelectedLatencyComponent(row)} />
                        </Tooltip>
                    );
                    actions.push(viewLatencyButton);
                }
                return actions;
            }
        }
    ];

    useEffect(() => {
        socket.on(`himsSystemComponentUpdate:${system.system_uuid}`, (components) => {
            setComponents(components);
        });
        socket.emit("getHimsComponentsFromSystemUuid", socket.id, system.system_uuid);

        return () => {
            socket.off(`himsSystemComponentUpdate:${system.system_uuid}`);
        };
    }, [socket]);

    return (
        <Card key={system.system_uuid}>
            <CardHeader
                title={getNameFromHimsSystem(system)}
                sx={{ pb: 1, borderBottom: 1, backgroundColor: getSystemColorFromType(system.status) }}
                slotProps={{ title: { noWrap: true }, content: { sx: { width: "100%" } } }}
            />
            <CardContent sx={{ py: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                {[
                    { title: "Status", value: system.status },
                    { title: "Date Reported", value: ConvertISOToDate(system.date_reported) },
                    { title: "# of Components", value: components.length },
                    { title: "Components Last Updated", value: components.length > 0 ? ConvertISOToDate(components[0].date_reported) : "" }
                ].map(({ title, value }, i) => (
                    <Box key={i} sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="bold" noWrap>
                            {`${title}:`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {value}
                        </Typography>
                    </Box>
                ))}
            </CardContent>

            <CardActions>
                {system.status_areas ? (
                    <Button size="small" onClick={() => setMapPreviewSystem(system)} sx={{ whiteSpace: "nowrap" }}>
                        View Map
                    </Button>
                ) : (
                    <></>
                )}
                <Button size="small" onClick={() => setSystemHistoryDialogOpen(true)} sx={{ mr: "auto", whiteSpace: "nowrap" }}>
                    View System History
                </Button>

                <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </CardActions>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ p: "8px !important", borderTop: 1 }}>
                    <Box
                        sx={{
                            overflowY: "auto",
                            "& .high": { backgroundColor: FAILURE_COLOR },
                            "& .medium": { backgroundColor: DEGRADED_OUTAGE_COLOR },
                            "& .low": { backgroundColor: DEGRADED_COLOR },
                            "& .nominal": { backgroundColor: ONLINE_COLOR }
                        }}
                    >
                        <ResponsiveGrid
                            disableVirtualization
                            rows={components}
                            columns={columns}
                            disableRowSelectionOnClick
                            autoHeight
                            rowHeight={30}
                            headerHeight={40}
                            getRowId={(row) => row.sensor_uuid}
                            pagination
                            slots={{ pagination: CustomPagination }}
                            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            getCellClassName={(params) => {
                                if (params.field === "severity") {
                                    return params.value.toLowerCase();
                                }
                                return "";
                            }}
                        />
                    </Box>
                </CardContent>
            </Collapse>

            {mapPreviewSystem !== null ? <HimsMapPreview mapSystem={mapPreviewSystem} setMapSystem={setMapPreviewSystem} /> : <></>}

            {systemHistoryDialogOpen ? (
                <HimsSystemHistoryDialog
                    system={system}
                    systemHistoryDialogOpen={systemHistoryDialogOpen}
                    setSystemHistoryDialogOpen={setSystemHistoryDialogOpen}
                />
            ) : (
                <></>
            )}
            {selectedFaultComponent ? (
                <HimsFaultDialog selectedFaultComponent={selectedFaultComponent} setSelectedFaultComponent={setSelectedFaultComponent} />
            ) : (
                <></>
            )}
            {selectedLatencyComponent ? (
                <HimsLatencyDialog selectedLatencyComponent={selectedLatencyComponent} setSelectedLatencyComponent={setSelectedLatencyComponent} />
            ) : (
                <></>
            )}
        </Card>
    );
};
export default HimsSystemCard;
